import React, { FC, memo, useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';

import { modalActions } from '@contexts/common/ModalContext';

import usePublishVaultAction from '@hooks/vaults/usePublishVaultAction';

import { infoRequiredYup } from '@validators/vaults/common.validators';

import Button from '@components/common/Button';
import Input from '@components/common/Input';

import {
  DEFAULT_CRITERIA_MODE,
  DEFAULT_REVALIDATE_MODE,
  FieldNames,
} from '@constants/common/form.constants';
import { MODAL_TYPES } from '@constants/common/modals.constants';
import { Variant } from '@constants/common/ui.constants';

import { VaultInformationProps } from '@app-types/common/modal.types';

import useStyles from './styles.module';

type FormValues = {
  [FieldNames.Title]: string;
  [FieldNames.PublisherName]: string;
  [FieldNames.Description]: string;
};

type EditVaultInformationProps = VaultInformationProps & {
  onOpenForm: () => void;
};

const EditForm: FC<EditVaultInformationProps> = ({ vault, onOpenForm }) => {
  const { classes } = useStyles();
  const {
    title: savedTitle,
    description: savedDescription,
    publisherName: savedPublisherName,
  } = vault;

  const { sign: editInfo, isPending } = usePublishVaultAction(vault);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        [FieldNames.Title]: infoRequiredYup(20, 3),
        [FieldNames.PublisherName]: infoRequiredYup(20, 3),
        [FieldNames.Description]: infoRequiredYup(120, 3),
      }),
    [],
  );

  const { control, handleSubmit, formState } = useForm<FormValues>({
    mode: DEFAULT_CRITERIA_MODE,
    reValidateMode: DEFAULT_REVALIDATE_MODE,
    resolver: yupResolver(validationSchema),
    defaultValues: {
      [FieldNames.Title]: savedTitle,
      [FieldNames.PublisherName]: savedPublisherName,
      [FieldNames.Description]: savedDescription,
    },
  });

  const { isDirty, isSubmitting } = formState;

  const onSubmit = useCallback(
    async (formData: FormValues) => {
      const { title, publisherName, description } = formData;

      await editInfo(title, publisherName, description);
      onOpenForm();
      modalActions.show(MODAL_TYPES.VAULT_INFORMATION, {
        vault: {
          ...vault,
          title,
          publisherName,
          description,
        },
      });
    },
    [editInfo, onOpenForm, vault],
  );

  return (
    <form className={classes.content} onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name={FieldNames.Title}
        render={({
          field: { value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <Input
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            placeholder="Vault name"
            inputError={error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name={FieldNames.PublisherName}
        render={({
          field: { value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <Input
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            placeholder="Publisher name"
            inputError={error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name={FieldNames.Description}
        render={({
          field: { value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <Input
            value={value}
            onBlur={onBlur}
            onChange={onChange}
            placeholder="Description"
            rows={3}
            inputError={error?.message}
          />
        )}
      />
      <div className={classes.buttons}>
        <Button
          onClick={onOpenForm}
          variant={Variant.Secondary}
          buttonType="submit"
          fullWidth
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          buttonType="submit"
          fullWidth
          isLoading={isPending}
          disabled={!isDirty}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default memo(EditForm);
